'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useSpeechToText } from '@/lib/hooks/useSpeechToText';
import { createClient } from '@/lib/supabase/client';
import toast from 'react-hot-toast';
import {
  Mic,
  Square,
  Play,
  Pause,
  RotateCcw,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Upload,
  Loader2,
  Volume2,
  FileText,
  X,
  Maximize2,
  CloudUpload,
} from 'lucide-react';

type RecordingState = 'idle' | 'recording' | 'paused' | 'stopped';

interface AudioChunk {
  blob: Blob;
  duration: number;
}

export default function FloatingRecordButton() {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [audioChunk, setAudioChunk] = useState<AudioChunk | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcribedText, setTranscribedText] = useState('');
  const [editedText, setEditedText] = useState('');
  const [step, setStep] = useState<'record' | 'review'>('record');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string | null>(null);
  const [uploadedAudioPath, setUploadedAudioPath] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedElapsedRef = useRef<number>(0);

  const { text, isLoading: isTranscribing, error: transcribeError, transcribe } = useSpeechToText(
    'OPEN_AI',
    'gpt-4o-transcribe'
  );

  useEffect(() => {
    if (transcribeError) {
      toast.error('Transcription failed — your audio is still saved. You can continue without a transcript.');
    }
  }, [transcribeError]);

  useEffect(() => {
    if (text) {
      setTranscribedText(text);
      setEditedText(text);
      setStep('review');
    }
  }, [text]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  // Close modal with Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now() - pausedElapsedRef.current * 1000;
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 500);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const uploadAudioToStorage = useCallback(async (blob: Blob, mimeType: string) => {
    setIsUploadingAudio(true);
    try {
      const supabase = createClient();
      const ext = mimeType.includes('webm') ? 'webm' : mimeType.includes('ogg') ? 'ogg' : 'webm';
      const fileName = `oral-submission-${Date.now()}.${ext}`;
      const filePath = `oral-submissions/${fileName}`;

      const { error } = await supabase.storage
        .from('contribution-media')
        .upload(filePath, blob, { contentType: mimeType, upsert: false });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('contribution-media')
        .getPublicUrl(filePath);

      setUploadedAudioUrl(urlData.publicUrl);
      setUploadedAudioPath(filePath);
      toast.success('Audio saved to storage!');
      return urlData.publicUrl;
    } catch (err) {
      console.error('Audio upload error:', err);
      toast.error('Could not save audio to storage. You can still continue.');
      return null;
    } finally {
      setIsUploadingAudio(false);
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm' : 'audio/ogg';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioChunk({ blob, duration: elapsed });
        setAudioUrl(url);
        stream.getTracks().forEach((t) => t.stop());
        // Auto-upload audio to storage immediately after stopping
        await uploadAudioToStorage(blob, mimeType);
      };

      recorder.start(250);
      pausedElapsedRef.current = 0;
      setElapsed(0);
      setRecordingState('recording');
      startTimer();
    } catch {
      setPermissionDenied(true);
      toast.error('Microphone access denied. Please allow microphone access and try again.');
    }
  }, [elapsed, startTimer, uploadAudioToStorage]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      pausedElapsedRef.current = elapsed;
      stopTimer();
      setRecordingState('paused');
    }
  }, [elapsed, stopTimer]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingState('recording');
      startTimer();
    }
  }, [startTimer]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      stopTimer();
      setRecordingState('stopped');
    }
  }, [stopTimer]);

  const resetRecording = useCallback(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setAudioChunk(null);
    setRecordingState('idle');
    setElapsed(0);
    pausedElapsedRef.current = 0;
    setTranscribedText('');
    setEditedText('');
    setStep('record');
    setUploadedAudioUrl(null);
    setUploadedAudioPath(null);
    chunksRef.current = [];
    mediaRecorderRef.current = null;
  }, [audioUrl]);

  const handleTranscribe = useCallback(async () => {
    if (!audioChunk) return;
    const file = new File([audioChunk.blob], 'recording.webm', { type: audioChunk.blob.type });
    const result = await transcribe(file);
    // If transcription fails, still move to review step with empty text
    if (!result?.text) {
      setStep('review');
    }
  }, [audioChunk, transcribe]);

  const handleSkipTranscription = useCallback(() => {
    setStep('review');
  }, []);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleAudioEnded = useCallback(() => setIsPlaying(false), []);

  const handleContinueToSubmission = useCallback(() => {
    const params = new URLSearchParams();
    if (editedText.trim()) {
      params.set('voice_transcript', editedText);
    }
    if (uploadedAudioUrl) {
      params.set('audio_url', uploadedAudioUrl);
    }
    if (audioChunk) {
      params.set('audio_duration', String(audioChunk.duration));
      params.set('audio_file_size', String(audioChunk.blob.size));
      params.set('audio_uploaded_at', new Date().toISOString());
    }
    params.set('certainty', 'firsthand_account');
    const href = `/contribution-submission?${params.toString()}`;
    setOpen(false);
    resetRecording();
    router.push(href);
  }, [editedText, uploadedAudioUrl, audioChunk, resetRecording, router]);

  const handleOpenFullPage = useCallback(() => {
    setOpen(false);
    router.push('/record-story');
  }, [router]);

  const handleClose = useCallback(() => {
    setOpen(false);
    // Don't reset so user can reopen and continue
  }, []);

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Record your story"
        title="Record your story"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105 active:scale-95 group"
        style={{
          backgroundColor: 'var(--primary)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(204,0,0,0.35)',
        }}
      >
        <Mic className="w-5 h-5" />
        <span className="text-sm font-semibold pr-1">Record Story</span>
      </button>

      {/* Modal Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <div
            className="relative w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
            style={{
              backgroundColor: 'var(--background)',
              maxHeight: '90vh',
              boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
            }}
          >
            {/* Modal Header */}
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: 'var(--border)', backgroundColor: 'white' }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  <Mic className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>
                    Record Your Story
                  </h2>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    Your audio is saved — transcription is optional
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleOpenFullPage}
                  title="Open full recording page"
                  className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleClose}
                  title="Close"
                  className="p-1.5 rounded-md transition-colors hover:bg-gray-100"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Step tabs */}
              <div className="flex items-center gap-2 text-xs">
                <ModalStepDot num={1} label="Record" active={step === 'record'} done={step === 'review'} />
                <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
                <ModalStepDot num={2} label="Review & Submit" active={step === 'review'} done={false} />
              </div>

              {/* ── STEP 1: RECORD ── */}
              {step === 'record' && (
                <div className="space-y-4">
                  {permissionDenied && (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>Microphone access denied. Allow microphone access in your browser settings and reload.</span>
                    </div>
                  )}

                  {/* Recorder card */}
                  <div
                    className="rounded-xl p-6 flex flex-col items-center gap-4 border"
                    style={{ backgroundColor: 'white', borderColor: 'var(--border)' }}
                  >
                    {/* Waveform */}
                    <div className="w-full flex items-center justify-center h-14">
                      <ModalWaveform active={recordingState === 'recording'} />
                    </div>

                    {/* Timer */}
                    <div
                      className="text-4xl font-mono font-semibold tabular-nums tracking-tight"
                      style={{ color: 'var(--foreground)' }}
                    >
                      {formatTime(elapsed)}
                    </div>

                    {/* Status */}
                    <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {recordingState === 'idle' && <span>Ready to record</span>}
                      {recordingState === 'recording' && (
                        <>
                          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--primary)' }} />
                          <span className="font-medium" style={{ color: 'var(--primary)' }}>Recording…</span>
                        </>
                      )}
                      {recordingState === 'paused' && <span className="text-amber-600 font-medium">Paused</span>}
                      {recordingState === 'stopped' && (
                        isUploadingAudio ? (
                          <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Saving audio…
                          </span>
                        ) : uploadedAudioUrl ? (
                          <span className="flex items-center gap-1.5 text-green-700 font-medium">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Audio saved
                          </span>
                        ) : (
                          <span className="text-green-700 font-medium">Recording complete</span>
                        )
                      )}
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-3">
                      {recordingState === 'idle' && (
                        <button
                          onClick={startRecording}
                          className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white font-semibold text-sm transition-colors shadow-md"
                          style={{ backgroundColor: 'var(--primary)' }}
                        >
                          <Mic className="w-4 h-4" />
                          Start Recording
                        </button>
                      )}

                      {(recordingState === 'recording' || recordingState === 'paused') && (
                        <>
                          {recordingState === 'recording' ? (
                            <button
                              onClick={pauseRecording}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors hover:bg-gray-50"
                              style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                            >
                              <Pause className="w-3.5 h-3.5" />
                              Pause
                            </button>
                          ) : (
                            <button
                              onClick={resumeRecording}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors hover:bg-gray-50"
                              style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                            >
                              <Mic className="w-3.5 h-3.5" />
                              Resume
                            </button>
                          )}
                          <button
                            onClick={stopRecording}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-medium transition-opacity hover:opacity-80"
                            style={{ backgroundColor: 'var(--foreground)' }}
                          >
                            <Square className="w-3.5 h-3.5" />
                            Stop
                          </button>
                        </>
                      )}

                      {recordingState === 'stopped' && (
                        <button
                          onClick={resetRecording}
                          disabled={isUploadingAudio}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
                          style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Re-record
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Playback + transcribe */}
                  {recordingState === 'stopped' && audioUrl && (
                    <div
                      className="rounded-xl p-4 space-y-3 border"
                      style={{ backgroundColor: 'white', borderColor: 'var(--border)' }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <button
                            onClick={handlePlayPause}
                            className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-gray-100"
                            style={{ backgroundColor: 'var(--secondary)' }}
                          >
                            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                          </button>
                          <div>
                            <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>Your recording</p>
                            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{formatTime(audioChunk?.duration ?? elapsed)}</p>
                          </div>
                        </div>
                        {uploadedAudioUrl ? (
                          <span className="flex items-center gap-1 text-xs text-green-700 font-medium">
                            <CloudUpload className="w-3.5 h-3.5" />
                            Saved
                          </span>
                        ) : (
                          <Volume2 className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
                        )}
                      </div>
                      <audio ref={audioRef} src={audioUrl} onEnded={handleAudioEnded} className="hidden" />

                      {/* Transcribe button */}
                      <button
                        onClick={handleTranscribe}
                        disabled={isTranscribing || isUploadingAudio}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ backgroundColor: 'var(--primary)' }}
                      >
                        {isTranscribing ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Transcribing…
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4" />
                            Transcribe Recording (Optional)
                          </>
                        )}
                      </button>

                      {/* Skip transcription — go straight to review */}
                      <button
                        onClick={handleSkipTranscription}
                        disabled={isUploadingAudio}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors hover:bg-gray-50 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
                      >
                        <ArrowRight className="w-4 h-4" />
                        Skip — Continue with Audio Only
                      </button>
                    </div>
                  )}

                  {/* Upload alternative */}
                  <ModalUploadAlternative
                    onFile={async (file) => {
                      const url = URL.createObjectURL(file);
                      setAudioChunk({ blob: file, duration: 0 });
                      setAudioUrl(url);
                      setRecordingState('stopped');
                      await uploadAudioToStorage(file, file.type);
                    }}
                  />
                </div>
              )}

              {/* ── STEP 2: REVIEW ── */}
              {step === 'review' && (
                <div className="space-y-4">
                  {/* Audio saved banner */}
                  {uploadedAudioUrl && (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-xs">
                      <CloudUpload className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>Your audio recording has been saved. It will be attached to your submission.</span>
                    </div>
                  )}

                  {transcribedText ? (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-50 border border-green-200 text-green-800 text-xs">
                      <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>Transcription complete. Review and edit below, then continue to the submission form.</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>No transcript — your audio file is saved and will be attached to your submission. You can add notes below.</span>
                    </div>
                  )}

                  <div
                    className="rounded-xl p-4 border space-y-2"
                    style={{ backgroundColor: 'white', borderColor: 'var(--border)' }}
                  >
                    <label className="block text-xs font-semibold" style={{ color: 'var(--foreground)' }}>
                      {transcribedText ? 'Your transcribed account' : 'Add notes (optional)'}
                      <span className="ml-1.5 font-normal" style={{ color: 'var(--muted-foreground)' }}>
                        {transcribedText ? '— edit freely' : '— describe your oral submission'}
                      </span>
                    </label>
                    <textarea
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      rows={8}
                      className="w-full rounded-lg border px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2"
                      style={{
                        borderColor: 'var(--border)',
                        backgroundColor: 'var(--input)',
                        color: 'var(--foreground)',
                      }}
                      placeholder={transcribedText ? 'Your transcribed story will appear here…' : 'Add any notes about your oral submission…'}
                    />
                    {editedText.trim() && (
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                        {editedText.trim().split(/\s+/).filter(Boolean).length} words
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={resetRecording}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full border text-xs transition-colors hover:bg-gray-50"
                      style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Re-record
                    </button>
                    <button
                      onClick={handleContinueToSubmission}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-semibold text-sm transition-colors"
                      style={{ backgroundColor: 'var(--primary)' }}
                    >
                      Continue to Submission
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Sub-components ──

function ModalStepDot({ num, label, active, done }: { num: number; label: string; active: boolean; done: boolean }) {
  return (
    <div className="flex items-center gap-1">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition-colors`}
        style={{
          backgroundColor: done ? '#16a34a' : active ? 'var(--primary)' : 'var(--muted)',
          color: done || active ? 'white' : 'var(--muted-foreground)',
        }}
      >
        {done ? <CheckCircle className="w-3 h-3" /> : num}
      </div>
      <span
        className="text-xs font-medium"
        style={{ color: active ? 'var(--foreground)' : 'var(--muted-foreground)' }}
      >
        {label}
      </span>
    </div>
  );
}

function ModalWaveform({ active }: { active: boolean }) {
  const bars = Array.from({ length: 22 });
  return (
    <div className="flex items-center gap-[3px] h-12">
      {bars.map((_, i) => (
        <div
          key={i}
          className="w-1.5 rounded-full transition-all"
          style={{
            backgroundColor: active ? 'var(--primary)' : 'var(--muted)',
            height: active
              ? `${16 + Math.sin(i * 0.7) * 14 + Math.cos(i * 1.3) * 8}px`
              : `${6 + Math.sin(i * 0.5) * 3}px`,
            transition: active ? 'height 0.3s ease' : 'height 0.6s ease',
          }}
        />
      ))}
    </div>
  );
}

function ModalUploadAlternative({ onFile }: { onFile: (file: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('audio/')) {
      toast.error('Please upload an audio file (mp3, wav, m4a, webm, etc.)');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Audio file must be under 25 MB.');
      return;
    }
    onFile(file);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
      }}
      className="border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors"
      style={{
        borderColor: dragging ? 'var(--primary)' : 'var(--border)',
        backgroundColor: dragging ? '#CC000008' : 'transparent',
      }}
      onClick={() => inputRef.current?.click()}
    >
      <Upload className="w-5 h-5 mx-auto mb-1.5" style={{ color: 'var(--muted-foreground)' }} />
      <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>Or upload an existing audio file</p>
      <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>mp3, wav, m4a, webm · max 25 MB</p>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}
